/*
  component-glossee-ugc-videos.js — Glossée UGC Videos rail (Phase 3.6; section and Product information block since 3.6.2)

  Behaviour measured on the us.doonails.com product page social strip, rebuilt for Shopify-hosted MP4 files:
  - previews: a tile gets its muted, looped preview only while at least half of it is on screen, one tile at a time
    every 450ms, at most 5 at once (3 at max-width 640px); a tile that leaves the screen is paused. The poster stays
    until the video really plays. Nothing plays with prefers-reduced-motion, Save-Data or the "cover only" setting.
  - a click (or Enter / Space) opens the large player: the sharper rendition with sound and controls, Escape / the
    close button / the backdrop close it, focus stays inside while it is open and returns to the tile afterwards,
    the page does not scroll behind it, and the video is unloaded on close so no sound or download keeps running.
  - desktop arrows scroll by the number of whole tiles on screen and hide at either end.
  - YouTube tiles (Phase 3.6.3) carry only a poster and the same play button: the iframe is built on click inside the
    same large player and the node is removed again on close, so playback and every youtube.com connection stop with
    it and a page that shows YouTube tiles still loads without a single iframe or YouTube request.
  - a YouTube tile whose block turns "YouTube önizlemesini otomatik oynat" on (Phase 3.6.4) also plays muted on the
    card: a second observer mounts one muted player in the card that is most on screen, at most ONE per rail, and
    empties the mount as soon as that card falls below half visible, so scrolling never leaves players running and a
    card below the fold still costs nothing until it arrives. It obeys the same gates as the MP4 previews — the
    section's "cover only" choice, prefers-reduced-motion and Save-Data — and never asks for sound.
  - that card preview is meant to read as ours, not as a YouTube player (Phase 3.9.1): the iframe is inert
    (pointer-events: none, controls off) and our own button sits above it, so the visitor's mouse never reaches the
    player and its hover chrome is never triggered. When the clip reaches its end the player would otherwise put its
    own replay/branding screen on the card, which is cross-origin and cannot be styled away, so the player reports
    the end over the documented postMessage channel and we take the iframe out and give the poster back.
  One instance per section; binds in connectedCallback, unbinds in disconnectedCallback (Theme Editor reloads).
  No dependencies and no third-party requests until a visitor opens a YouTube tile.
*/
(function () {
  'use strict';
  if (customElements.get('glossee-ugc-videos')) return;

  var START_GAP = 450;
  /* privacy-enhanced embed: YouTube sets no tracking cookie until the video actually plays */
  var YT_ORIGIN = 'https://www.youtube-nocookie.com';
  var YT_EMBED = YT_ORIGIN + '/embed/';
  /* player states on the iframe messaging channel; 0 is ENDED, which is the only one we act on */
  var YT_ENDED = 0;
  var MOBILE = '(max-width: 640px)';
  var REDUCE = '(prefers-reduced-motion: reduce)';

  function saveData() {
    var connection = navigator.connection;
    return !!(connection && connection.saveData);
  }

  class GlosseeUgcVideos extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-gugc-rail]');
      this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-gugc-tile]'));
      this.prevButton = this.querySelector('[data-gugc-prev]');
      this.nextButton = this.querySelector('[data-gugc-next]');
      this.modal = this.querySelector('[data-gugc-modal]');
      if (!this.rail) return;

      this.queue = [];
      this.starting = false;
      this.modalOpen = false;
      this.lastTile = null;
      this.ytActiveTile = null;

      this.onRailScroll = this.sync.bind(this);
      this.onRailClick = this.handleRailClick.bind(this);
      this.onPrev = this.scrollByPage.bind(this, -1);
      this.onNext = this.scrollByPage.bind(this, 1);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onModalClick = this.handleModalClick.bind(this);
      this.onVisibility = this.handleVisibility.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.rail.addEventListener('scroll', this.onRailScroll, { passive: true });
      this.rail.addEventListener('click', this.onRailClick);
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.sync.bind(this));
        this.resizeObserver.observe(this.rail);
      }
      this.sync();

      this.tiles.forEach(function (tile) {
        /* A YouTube tile without a merchant cover asks for maxresdefault, which not every video has. A missing
           rendition is not an error: i.ytimg.com answers it with a grey 120x90 placeholder and HTTP 200, so the
           load event fires and only the size tells the two apart (a real maxresdefault is 1280 wide). Anything
           120px or narrower — placeholder or genuine failure — is swapped for mqdefault, which always exists. */
        var poster = tile.querySelector('img[data-gugc-poster-fallback]');
        if (poster && poster.dataset.gugcPosterFallback) {
          var settle = function () {
            var next = poster.dataset.gugcPosterFallback;
            if (!next) return;
            delete poster.dataset.gugcPosterFallback;
            if (poster.naturalWidth > 120) return;
            if (poster.getAttribute('src') !== next) poster.setAttribute('src', next);
          };
          if (poster.complete) {
            settle();
          } else {
            poster.addEventListener('load', settle, { once: true });
            poster.addEventListener('error', settle, { once: true });
          }
        }

        var video = tile.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.addEventListener('playing', function () {
          if (tile.gugcActive) tile.classList.add('is-previewing');
        });
        video.addEventListener('pause', function () {
          tile.classList.remove('is-previewing');
        });
      });

      this.setupPreviews();
      this.setupYouTubePreviews();
    }

    disconnectedCallback() {
      if (!this.rail) return;
      this.closeModal(true);
      this.rail.removeEventListener('scroll', this.onRailScroll);
      this.rail.removeEventListener('click', this.onRailClick);
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.observer) this.observer.disconnect();
      if (this.ytObserver) this.ytObserver.disconnect();
      if (this.onYtMessage) window.removeEventListener('message', this.onYtMessage);
      this.stopYouTubePreview();
      this.tiles.forEach(this.stopPreview.bind(this));
      if (this.modal && this.modal.parentNode === document.body) this.modal.remove();
    }

    /* ---------------------------------------------------------------- rail */
    sync() {
      var max = this.rail.scrollWidth - this.rail.clientWidth;
      this.classList.toggle('has-overflow', max > 1);
      if (this.prevButton) this.prevButton.disabled = this.rail.scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = this.rail.scrollLeft >= max - 1;
    }

    scrollByPage(direction) {
      var item = this.rail.firstElementChild;
      if (!item) return;
      var gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      var step = item.getBoundingClientRect().width + gap;
      var whole = Math.max(1, Math.floor((this.rail.clientWidth + gap) / step));
      this.rail.scrollBy({ left: direction * whole * step, behavior: window.matchMedia(REDUCE).matches ? 'auto' : 'smooth' });
    }

    handleRailClick(event) {
      var tile = event.target.closest('[data-gugc-tile]');
      if (!tile || !this.rail.contains(tile)) return;
      event.preventDefault();
      this.openModal(tile);
    }

    handleBlockSelect(event) {
      /* only tiles of the section version are editor blocks; the product-information block selects the whole rail */
      if (!this.rail.contains(event.target)) return;
      var left = event.target.offsetLeft - this.rail.offsetLeft;
      this.rail.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
    }

    /* ---------------------------------------------------------------- previews */
    setupPreviews() {
      if (this.dataset.preview !== 'autoplay') return;
      if (window.matchMedia(REDUCE).matches || saveData()) return;
      if (!('IntersectionObserver' in window)) return;

      var self = this;
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var tile = entry.target;
          if (!entry.isIntersecting) {
            tile.gugcInView = false;
            self.stopPreview(tile);
            return;
          }
          if (entry.intersectionRatio < 0.5) return;
          tile.gugcInView = true;
          if (!tile.gugcActive && self.queue.indexOf(tile) < 0) self.queue.push(tile);
        });
        self.pump();
      }, { threshold: [0, 0.5] });

      this.tiles.forEach(function (tile) {
        if (tile.querySelector('video[data-src]')) self.observer.observe(tile);
      });
    }

    /* ---------------------------------------------------------------- YouTube card previews */
    setupYouTubePreviews() {
      this.ytTiles = this.tiles.filter(function (tile) {
        return tile.dataset.yt && tile.dataset.ytAutoplay === 'true';
      });
      if (!this.ytTiles.length) return;
      /* the same three gates the MP4 previews use, so one "cover only" switch still silences the whole rail */
      if (this.dataset.preview !== 'autoplay') return;
      if (window.matchMedia(REDUCE).matches || saveData()) return;
      if (!('IntersectionObserver' in window)) return;

      var self = this;
      this.ytObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.gugcYtRatio = entry.isIntersecting ? entry.intersectionRatio : 0;
          /* a card that has left is allowed to play again the next time it comes back */
          if (entry.target.gugcYtRatio < 0.5) entry.target.gugcYtEnded = false;
        });
        self.syncYouTubePreview();
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });

      this.ytTiles.forEach(function (tile) { self.ytObserver.observe(tile); });

      this.onYtMessage = this.handleYouTubeMessage.bind(this);
      window.addEventListener('message', this.onYtMessage);
    }

    /* The player tells us when the clip is over. This is the same channel the YouTube IFrame API uses, spoken
       directly so no extra script has to be downloaded: the frame is opened with enablejsapi=1, we say we are
       listening once it loads, and it answers with state changes. Every message is checked against the frame we
       opened and the YouTube origin before it is read. If any of this ever stops arriving the preview simply keeps
       playing to its end as it did before — the card is never left broken. */
    handleYouTubeMessage(event) {
      if (!this.ytFrame || event.source !== this.ytFrame.contentWindow) return;
      if (event.origin !== YT_ORIGIN) return;

      var data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (error) {
        return;
      }
      if (!data || !data.event) return;

      var state = null;
      if (data.event === 'onStateChange') state = data.info;
      else if (data.event === 'infoDelivery' && data.info && data.info.playerState !== undefined) state = data.info.playerState;
      if (state !== YT_ENDED) return;

      /* hold this card at its poster until it has been off screen once, otherwise the observer would
         immediately hand the player back to the same card and it would loop through its end screen */
      if (this.ytActiveTile) this.ytActiveTile.gugcYtEnded = true;
      this.stopYouTubePreview();
    }

    syncYouTubePreview() {
      if (!this.ytObserver || !this.ytTiles) return;
      if (this.modalOpen || document.hidden) {
        this.stopYouTubePreview();
        return;
      }
      /* deterministic and deliberately conservative: one player per rail, given to the card that is most on screen,
         ties to the earliest card in the rail. A card under half visible never holds the player. */
      var best = null;
      var bestRatio = 0;
      this.ytTiles.forEach(function (tile) {
        var ratio = tile.gugcYtRatio || 0;
        if (ratio < 0.5 || tile.gugcYtEnded) return;
        if (ratio > bestRatio) {
          best = tile;
          bestRatio = ratio;
        }
      });
      if (best === this.ytActiveTile) return;
      this.stopYouTubePreview();
      if (best) this.startYouTubePreview(best);
    }

    startYouTubePreview(tile) {
      var item = tile.parentNode;
      var mount = item ? item.querySelector('[data-gugc-yt-preview]') : null;
      if (!mount) return;
      var id = tile.dataset.yt;
      var frame = document.createElement('iframe');
      frame.className = 'gugc__ytframe';
      /* mute=1 is what lets this start without a gesture. No loop: looping a single video needs playlist=<id>, and
         a playlist parameter on youtube-nocookie.com makes the player answer "Video unavailable" (a known
         limitation of the privacy-enhanced domain), so the preview plays once and the poster comes back at the end.
         controls/disablekb/fs/iv_load_policy strip what the embed lets us strip; enablejsapi opens the channel that
         tells us the clip finished. origin is the page we are on, which is what YouTube asks for with enablejsapi. */
      frame.src = YT_EMBED + encodeURIComponent(id) +
        '?autoplay=1&mute=1&playsinline=1&controls=0&rel=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3' +
        '&enablejsapi=1&origin=' + encodeURIComponent(window.location.origin);
      frame.setAttribute('allow', 'autoplay; encrypted-media');
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      /* decorative: the card's own button carries the name and the click */
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('title', '');
      /* say we are listening as soon as the player exists, so it starts reporting its state */
      frame.addEventListener('load', function () {
        try {
          frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), YT_ORIGIN);
        } catch (error) {
          /* nothing to do: without the channel the preview just plays to its end as before */
        }
      }, { once: true });

      mount.innerHTML = '';
      mount.appendChild(frame);
      item.classList.add('is-yt-previewing');
      this.ytActiveTile = tile;
      this.ytFrame = frame;
    }

    stopYouTubePreview() {
      var tile = this.ytActiveTile;
      this.ytFrame = null;
      if (!tile) return;
      this.ytActiveTile = null;
      var item = tile.parentNode;
      if (!item) return;
      item.classList.remove('is-yt-previewing');
      var mount = item.querySelector('[data-gugc-yt-preview]');
      /* emptying the mount is what stops playback and releases the player and its connections */
      if (mount) mount.innerHTML = '';
    }

    activeCount() {
      return this.tiles.filter(function (tile) { return tile.gugcActive; }).length;
    }

    pump() {
      if (this.starting || this.modalOpen || document.hidden) return;
      var limit = window.matchMedia(MOBILE).matches ? 3 : 5;
      if (this.activeCount() >= limit) return;
      var tile = this.queue.shift();
      if (!tile) return;
      if (!tile.gugcInView || tile.gugcActive) {
        this.pump();
        return;
      }
      this.starting = true;
      this.startPreview(tile);
      var self = this;
      window.setTimeout(function () {
        self.starting = false;
        self.pump();
      }, START_GAP);
    }

    startPreview(tile) {
      var video = tile.querySelector('video[data-src]');
      if (!video) return;
      if (!video.getAttribute('src')) video.setAttribute('src', video.dataset.src);
      tile.gugcActive = true;
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          tile.gugcActive = false;
          tile.classList.remove('is-previewing');
        });
      }
    }

    stopPreview(tile) {
      var index = this.queue ? this.queue.indexOf(tile) : -1;
      if (index > -1) this.queue.splice(index, 1);
      tile.gugcActive = false;
      tile.classList.remove('is-previewing');
      var video = tile.querySelector('video[data-src]');
      if (video && !video.paused) video.pause();
    }

    pauseAllPreviews() {
      this.tiles.forEach(function (tile) {
        var video = tile.querySelector('video[data-src]');
        if (video && !video.paused) video.pause();
      });
    }

    resumePreviews() {
      if (this.modalOpen || document.hidden) return;
      this.tiles.forEach(function (tile) {
        if (!tile.gugcActive || !tile.gugcInView) return;
        var video = tile.querySelector('video[data-src]');
        if (video && video.paused) {
          var attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
        }
      });
      this.pump();
    }

    handleVisibility() {
      if (document.hidden) {
        this.pauseAllPreviews();
        this.stopYouTubePreview();
      } else {
        this.resumePreviews();
        this.syncYouTubePreview();
      }
    }

    /* ---------------------------------------------------------------- large player */
    openModal(tile) {
      if (!this.modal || (!tile.dataset.full && !tile.dataset.yt)) return;
      if (this.modal.parentNode !== document.body) document.body.appendChild(this.modal);

      this.modalOpen = true;
      this.lastTile = tile;
      this.pauseAllPreviews();
      /* before the large player exists, so a card preview and the modal are never both mounted */
      this.stopYouTubePreview();

      var player = this.modal.querySelector('[data-gugc-player]');
      var stage = this.modal.querySelector('[data-gugc-stage]');
      var card = this.modal.querySelector('[data-gugc-card]');
      var cta = this.modal.querySelector('[data-gugc-cta]');
      var ctaLabel = this.modal.querySelector('[data-gugc-cta-label]');

      stage.style.backgroundImage = tile.dataset.poster ? 'url("' + tile.dataset.poster + '")' : '';
      card.setAttribute('aria-label', tile.getAttribute('aria-label') || 'Müşteri videosu');

      if (tile.dataset.link && tile.dataset.linkLabel) {
        cta.href = tile.dataset.link;
        ctaLabel.textContent = tile.dataset.linkLabel;
        cta.hidden = false;
        card.style.setProperty('--gugc-cta-h', '52px');
      } else {
        cta.hidden = true;
        cta.removeAttribute('href');
        card.style.removeProperty('--gugc-cta-h');
      }

      var ytMount = this.modal.querySelector('[data-gugc-yt]');

      if (tile.dataset.yt) {
        /* YouTube: the player is created here and nowhere else, so nothing reaches youtube.com before this click */
        player.hidden = true;
        if (ytMount) {
          ytMount.innerHTML = '';
          ytMount.hidden = false;
          ytMount.appendChild(this.buildYouTube(tile.dataset.yt, card.getAttribute('aria-label')));
        }
      } else {
        if (ytMount) {
          ytMount.innerHTML = '';
          ytMount.hidden = true;
        }
        player.hidden = false;
        /* the cover fills the stage only until the video draws its first frame (no cover bands around a contained video) */
        player.addEventListener('playing', function clearCover() {
          stage.style.backgroundImage = '';
          player.removeEventListener('playing', clearCover);
        });
        player.muted = false;
        player.controls = true;
        player.playsInline = true;
        if (tile.dataset.poster) player.setAttribute('poster', tile.dataset.poster);
        player.setAttribute('src', tile.dataset.full);
      }

      this.modal.hidden = false;
      /* same compensation as the theme's own scroll lock: keep the page from shifting when the scrollbar goes */
      var scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--gugc-scrollbar', Math.max(0, scrollbar) + 'px');
      document.documentElement.classList.add('gugc-lock');
      this.modal.addEventListener('click', this.onModalClick);
      document.addEventListener('keydown', this.onKeydown);

      var closeButton = this.modal.querySelector('.gugc-modal__x');
      if (closeButton) closeButton.focus({ preventScroll: true });

      if (tile.dataset.yt) return;

      var attempt = player.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          /* the browser refused sound: start muted, the controls stay available to unmute */
          player.muted = true;
          var retry = player.play();
          if (retry && typeof retry.catch === 'function') retry.catch(function () {});
        });
      }
    }

    buildYouTube(id, label) {
      var frame = document.createElement('iframe');
      frame.className = 'gugc-modal__frame';
      /* autoplay is allowed here because this only ever runs from the visitor's own click on the tile */
      frame.src = YT_EMBED + encodeURIComponent(id) + '?autoplay=1&playsinline=1&rel=0&modestbranding=1';
      frame.title = label || 'Müşteri videosu';
      /* fullscreen is granted by the allowfullscreen attribute only: naming it in `allow` as well makes the
         browser warn that one overrides the other, and this is the pairing YouTube's own embed code uses */
      frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      frame.setAttribute('allowfullscreen', '');
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      frame.setAttribute('loading', 'eager');
      return frame;
    }

    closeModal(silent) {
      if (!this.modal || !this.modalOpen) return;
      var player = this.modal.querySelector('[data-gugc-player]');
      var stage = this.modal.querySelector('[data-gugc-stage]');
      if (player) {
        player.pause();
        player.removeAttribute('src');
        player.removeAttribute('poster');
        player.load();
        player.hidden = false;
      }
      /* removing the node is what actually stops YouTube playback and its connections */
      var ytMount = this.modal.querySelector('[data-gugc-yt]');
      if (ytMount) {
        ytMount.innerHTML = '';
        ytMount.hidden = true;
      }
      if (stage) stage.style.backgroundImage = '';

      this.modal.hidden = true;
      this.modalOpen = false;
      document.documentElement.classList.remove('gugc-lock');
      document.documentElement.style.removeProperty('--gugc-scrollbar');
      this.modal.removeEventListener('click', this.onModalClick);
      document.removeEventListener('keydown', this.onKeydown);

      var tile = this.lastTile;
      this.lastTile = null;
      if (!silent && tile && document.contains(tile)) tile.focus({ preventScroll: true });
      if (!silent) {
        this.resumePreviews();
        this.syncYouTubePreview();
      }
    }

    handleModalClick(event) {
      if (event.target.closest('[data-gugc-close]')) this.closeModal(false);
    }

    handleKeydown(event) {
      if (!this.modalOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.closeModal(false);
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(
        this.modal.querySelectorAll('.gugc-modal__x, [data-gugc-player]:not([hidden]), [data-gugc-yt] iframe, [data-gugc-cta]:not([hidden])')
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var inside = this.modal.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('glossee-ugc-videos', GlosseeUgcVideos);
})();
